/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useState } from 'react';
import axios from 'axios';
import { buildPortalIntegrationHeaders } from '@shared/utils/build-portal-integration-headers';
import { withAntiBotRetry } from '@shared/utils/with-antibot-retry';
import { API_BASE } from './constants';

/** What OLX said about the plan's slots when the dialog opened. */
export interface OlxBalance {
  available: number | null;
  renewsAt: string | null;
}

/**
 * Reads the OLX plan balance live (``POST .../settings/olx/plan``) when the
 * dialog opens, instead of trusting the last stored read: ads created on the
 * OLX site or a renewal change it in between. ``null`` while loading or when
 * OLX could not be read (the stored state then applies).
 *
 * @param agencyId - Agency id.
 * @param active - Whether OLX is offered in the dialog.
 * @returns The live balance, or null.
 */
export function useOlxBalance(agencyId: string, active: boolean): OlxBalance | null {
  const [balance, setBalance] = useState<OlxBalance | null>(null);
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    withAntiBotRetry(
      (headers) => axios.post(`${API_BASE}/portal-integration/agencies/${agencyId}/settings/olx/plan`, {}, { headers }),
      (opts) => buildPortalIntegrationHeaders('portal_settings', opts),
    )
      .then((res) => {
        const live = balanceFromPlan(res?.data);
        if (!cancelled && live) setBalance(live);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [agencyId, active]);
  return balance;
}

/** The balance of an active OLX plan response, or null for any other answer. */
function balanceFromPlan(data: unknown): OlxBalance | null {
  const plan = (data ?? {}) as { status?: unknown; ads_available?: unknown; renews_at?: unknown };
  if (plan.status !== 'active') return null;
  return {
    available: typeof plan.ads_available === 'number' ? plan.ads_available : null,
    renewsAt: typeof plan.renews_at === 'string' ? plan.renews_at : null,
  };
}

/**
 * The OLX block reason after the live balance: no slot left locks OLX for a
 * NEW ad (``olx_no_slots``); slots back clear a stale ``olx_no_slots``. An ad
 * already on OLX is edited in place and takes no slot, so it is never locked
 * for the quota. Other reasons (reconnect, terms, plan) are kept as they are.
 */
export function olxQuotaBlock(stored: string | null, balance: OlxBalance | null, hasAd: boolean): string | null {
  if (stored && stored !== 'olx_no_slots') return stored;
  if (hasAd) return null;
  return liveSlotsBlock(stored, balance?.available ?? null);
}

/** The quota block from the live slot count, keeping the stored one when OLX gave no count. */
function liveSlotsBlock(stored: string | null, available: number | null): string | null {
  if (available === null) return stored;
  return available === 0 ? 'olx_no_slots' : null;
}
